import { property } from '../config/property';
const consentKey = 'azalea-advertising-consent-v1';
const visitKey = 'azalea-attribution-v2';
const campaignKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'campaign_id', 'adset_id', 'ad_id', 'placement'];
let choice = '';
let initialized = false;
let pageViewed = false;
const sent = new Set();
const lower = (value) => String(value || '').trim().toLowerCase();
const safeGet = (key) => { try { return sessionStorage.getItem(key); } catch { return null; } };
const safeSet = (key, value) => { try { sessionStorage.setItem(key, value); } catch { /* Inquiry remains available. */ } };
const productionHost = () => ['azalearidgehome.com', 'www.azalearidgehome.com'].includes(window.location.hostname);
export function getAdvertisingConsent() {
  try { return choice || localStorage.getItem(consentKey) || ''; } catch { return choice; }
}
export function setAdvertisingConsent(allowed) {
  choice = allowed ? 'granted' : 'declined';
  try { localStorage.setItem(consentKey, choice); } catch { /* Keep the in-memory choice. */ }
  if (!allowed) {
    try { sessionStorage.removeItem(visitKey); } catch { /* No storage access. */ }
    try { window.fbq?.('consent', 'revoke'); } catch { /* Tracking is optional. */ }
  } else {
    try { window.fbq?.('consent', 'grant'); } catch { /* Tracking is optional. */ }
    initializeTracking();
  }
  window.dispatchEvent(new Event('advertising-consent-change'));
}
export const cleanPageUrl = () => `${window.location.origin}${window.location.pathname}`;
const cookie = (name) => document.cookie.split('; ').find((part) => part.startsWith(`${name}=`))?.slice(name.length + 1) || '';
const cleanCampaignValue = (value) => /^[A-Za-z0-9_. /:-]{1,160}$/.test(value || '') ? value : '';
export function getMarketingData() {
  const base = { source: 'unattributed', attribution_status: 'tracking_declined_or_unset', landing_page: cleanPageUrl() };
  if (getAdvertisingConsent() !== 'granted') return base;
  let visit = {};
  try { visit = JSON.parse(safeGet(visitKey) || '{}'); } catch { /* Ignore damaged storage. */ }
  const params = new URLSearchParams(window.location.search);
  campaignKeys.forEach((key) => { const value = cleanCampaignValue(params.get(key)); if (value) visit[key] = value; });
  // Only real platform cookies are used; no fabricated browser identifiers.
  const click = params.get('fbclid');
  if (/^[A-Za-z0-9_-]{1,500}$/.test(click || '')) visit.fbclid = click;
  if (!visit.landing_page) visit.landing_page = cleanPageUrl();
  let referrer = '';
  try { referrer = new URL(document.referrer).origin; } catch { /* Direct visit. */ }
  if (!visit.referrer) visit.referrer = referrer;
  const paid = visit.utm_medium === 'paid_social' && visit.utm_campaign === 'azalea_ridge_15d' && /^(fb|ig|facebook|instagram)$/.test(visit.utm_source || '');
  visit.source = paid ? 'paid_social' : visit.utm_source ? 'campaign_other' : referrer && referrer !== window.location.origin ? 'organic_referral' : 'direct';
  visit.attribution_status = 'consented';
  visit.ad_id = visit.ad_id || (/^\d+$/.test(visit.utm_content || '') ? visit.utm_content : '');
  visit.fbc = cookie('_fbc'); visit.fbp = cookie('_fbp');
  safeSet(visitKey, JSON.stringify(visit)); return visit;
}
export function initializeTracking() {
  if (getAdvertisingConsent() !== 'granted') return;
  getMarketingData();
  const pixelId = property.metaPixelId.trim();
  if (!productionHost() || !/^\d{5,20}$/.test(pixelId) || initialized) return;
  try {
    // The base pixel code in index.html already defines fbq, initializes the pixel and fires PageView.
    const preloaded = Boolean(window.fbq);
    if (!preloaded) {
      const fbq = function (...args) { if (fbq.callMethod) fbq.callMethod(...args); else fbq.queue.push(args); };
      fbq.queue = []; fbq.version = '2.0'; fbq.loaded = true; window.fbq = fbq; window._fbq = fbq;
      const script = document.createElement('script'); script.async = true; script.src = 'https://connect.facebook.net/en_US/fbevents.js'; document.head.appendChild(script);
    }
    initialized = true;
    if (!preloaded) {
      window.fbq('set', 'autoConfig', false, pixelId);
      window.fbq('init', pixelId);
    }
    window.fbq('consent', 'grant');
    if (!preloaded && !pageViewed) { pageViewed = true; window.fbq('track', 'PageView'); }
    window.fbq('track', 'ViewContent', { content_name: property.address, content_category: 'Real Estate' });
  } catch { /* Tracking errors must not affect inquiries. */ }
}
// QA / test traffic must never count as a real Meta conversion.
export function isQaSubmission(payload = {}) {
  try {
    const lead = payload?.lead || {};
    const marketing = payload?.marketing || {};
    const params = new URLSearchParams(window.location.search);
    const fullName = lower(`${lead.first_name || ''} ${lead.last_name || ''}`);
    return lower(marketing.source) === 'test'
      || lower(marketing.utm_source) === 'test'
      || lower(marketing.utm_campaign) === 'qa_exclude_from_results'
      || lower(params.get('utm_source')) === 'test'
      || lower(params.get('utm_campaign')) === 'qa_exclude_from_results'
      || lower(lead.email).endsWith('@example.invalid')
      || fullName.includes('qa test do not contact');
  } catch { return false; }
}
export function trackEvent(name, parameters = {}) {
  // The base pixel in index.html defines fbq. Only an explicit "declined" choice stops the Lead event.
  // Lead events are sent from the live site only, so local or preview testing never reaches the production pixel.
  if (name !== 'Lead' || getAdvertisingConsent() === 'declined' || typeof window.fbq !== 'function' || !productionHost()) return;
  const id = parameters.event_id;
  if (!/^lead_[a-zA-Z0-9_-]{20,100}$/.test(id || '') || sent.has(id) || safeGet(`azalea-sent:${id}`)) return;
  if (isQaSubmission(parameters.payload)) return;
  try {
    if (JSON.parse(localStorage.getItem('azalea-meta-leads-v1') || '[]').includes(id)) return;
  } catch { /* Fall back to session and in-memory deduplication. */ }
  sent.add(id); safeSet(`azalea-sent:${id}`, '1');
  try {
    const previous = JSON.parse(localStorage.getItem('azalea-meta-leads-v1') || '[]');
    localStorage.setItem('azalea-meta-leads-v1', JSON.stringify([...(Array.isArray(previous) ? previous.slice(-199) : []), id]));
  } catch { /* Storage may be unavailable. */ }
  // Allowlisted parameters exclude contacts, messages, financing details, value and currency.
  try { window.fbq('track', 'Lead', { content_name: parameters.content_name || property.address, content_category: 'Real Estate', content_type: 'property' }, { eventID: id }); } catch { /* Tracking errors must not affect inquiries. */ }
}
// Reusable entry point for a confirmed, successful property inquiry. Never throws.
export function trackLeadConversion({ eventId, propertyAddress = property.address, payload } = {}) {
  try {
    trackEvent('Lead', { event_id: eventId, content_name: propertyAddress, payload });
  } catch (error) {
    console.warn('Meta Lead tracking unavailable', error);
  }
}